import React from 'react';
import { Bathroom, Listing, RoomType } from "../../../api/listings-client";
import bathroomSvg from '../../../resources/bathroom.svg';
import billsSvg from '../../../resources/bills.svg';
import bondSvg from '../../../resources/bond.svg';
import calendarSvg from '../../../resources/calendar.svg';
import dollarSvg from '../../../resources/dollar.svg';
import furnitureSvg from '../../../resources/furniture.svg';
import parkingSvg from '../../../resources/parking.svg';
import roomSvg from '../../../resources/room.svg';
import EditButton from "./EditButton";
import ModalPrompt from "./ModalPrompt";
import { formatAvailableDate } from '../../../util/DateFormatterUtil';

import { MinimumStay, MaximumStay } from "../../../api/listings-client";

/**
 * Formats minimum and maximum stay durations into a readable string
 * Examples:
 * - "1 - 12+ months stay"
 * - "No minimum - 6 months stay"
 * - "6 months stay" (when min and max are the same)
 * - "12+ months stay" (for maximum stay only)
 */
export const formatStayDuration = (minimumStay?: MinimumStay, maximumStay?: MaximumStay): string => {
    if (!minimumStay || !maximumStay) {
        return "";
    }

    // Extract numeric values and unit (weeks/months) from the enum strings
    const parseStayValue = (stayValue: string): { value: number; unit: string; plus?: boolean } => {
        // Handle special case for "no minimum"
        if (stayValue === "no minimum") {
            return { value: 0, unit: "months" };
        }

        const hasPlus = stayValue.includes("+");
        // Remove the plus sign for parsing
        const cleanValue = stayValue.replace("+", "").trim();
        // Split into numeric value and unit
        const [valueStr, unit] = cleanValue.split(" ");

        return {
            value: parseInt(valueStr, 10),
            unit: unit,
            plus: hasPlus
        };
    };

    const minParsed = parseStayValue(minimumStay);
    const maxParsed = parseStayValue(maximumStay);

    // Ensure we're comparing the same units (convert weeks to months if needed)
    const normalizeToMonths = (value: number, unit: string): number => {
        return unit === "week" || unit === "weeks" ? value / 4 : value;
    };

    const minValueNormalized = normalizeToMonths(minParsed.value, minParsed.unit);
    const maxValueNormalized = normalizeToMonths(maxParsed.value, maxParsed.unit);

    // If minimum is "no minimum", use special text
    const minText = minimumStay === MinimumStay.NO_MINIMUM ? "No minimum" : minParsed.value.toString();

    // Add plus sign if present in the maximum stay
    const maxText = maxParsed.plus ? `${maxParsed.value}+` : maxParsed.value.toString();

    // Use the unit from the maximum stay (usually months)
    const unitText = maxParsed.unit;

    // If min and max are effectively the same or very close
    if (Math.abs(minValueNormalized - maxValueNormalized) < 0.1 && !maxParsed.plus) {
        return `${maxParsed.value} ${unitText} stay`;
    }

    // When minimum is "no minimum"
    if (minimumStay === MinimumStay.NO_MINIMUM) {
        return `No minimum - ${maxText} ${unitText} stay`;
    }

    // Standard case with different min and max
    return `${minText} - ${maxText} ${unitText} stay`;
};

interface DetailsSectionProps {
    localListing: Listing;
    onModalClick: (modalPrompt: ModalPrompt, initialTab?: 'Facilities' | 'Pricing' | 'Time') => void;
    editMode: boolean;
}

const DetailsSection: React.FC<DetailsSectionProps> = ({ localListing, onModalClick, editMode }) => {
    const DetailItem = ({ icon, text }: { icon: string; text: string }) => (
        <div className='flex items-center px-2 md:px-0 md:py-0.5'>
            <div className='w-[40px]'>
                <img
                    src={icon}
                    className='flex-shrink-0 size-6 md:size-7'
                    alt={`${text} icon`}
                />
            </div>
            <div className='align-middle p-2 font-normal md:font-medium text-gray-800'>
                {text}
            </div>
        </div>
    );

    const today = new Date().toISOString().split('T')[0];
    const availabilityText = localListing.availableFrom
        ? (new Date(localListing.availableFrom) <= new Date(today)
            ? 'Available now'
            : `Available ${formatAvailableDate(localListing.availableFrom)}`)
        : '';

    return (
        <div>
            <div className='flex'>
                <p className="text-xl font-medium py-6">Details</p>
                {editMode && <EditButton onClick={() => onModalClick(ModalPrompt.Details, 'Facilities')} modalPrompt={ModalPrompt.Details} />}
            </div>

            <div className='pb-6 w-full text-gray-800'>
                <div className='flex flex-col md:flex-row md:flex-wrap'>
                    <div className='w-full md:w-1/2'>
                        <DetailItem icon={dollarSvg} text={`$${localListing.rent} rent / week`} />
                        <DetailItem icon={bondSvg} text={`$${localListing.bond} bond`} />
                        <DetailItem icon={billsSvg} text={localListing.billsIncluded ? "Bills included" : "Bills not included"} />
                        <DetailItem icon={calendarSvg} text={availabilityText} />
                        <DetailItem icon={calendarSvg} text={formatStayDuration(localListing.minimumStay, localListing.maximumStay)} />
                    </div>
                    <div className='w-full md:w-1/2'>
                        <DetailItem icon={roomSvg} text={`${localListing.roomType === RoomType.SHARED ? "Shared" : "Private"} room`} />
                        <DetailItem icon={bathroomSvg} text={`${localListing.bathroom === Bathroom.SHARED ? "Shared" : "Private"} bathroom`} />
                        <DetailItem icon={furnitureSvg} text={localListing.furnished ? "Furnished" : "Unfurnished"} />
                        <DetailItem icon={parkingSvg} text={localListing.parking ? "Parking available" : "Parking not available"} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DetailsSection;